import { JSXElementConstructor, ReactNode } from "react";

import AlertIcon from "@uui/AlertCircle";
import NoVideoIcon from "@uui/VideoRecorderOff";

import { SvgIconProps, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";

import ROUTES from "@app/common/routes";

import IconBackground from "@rui/IconBackground";
import Link from "@rui/Link";
import Stack from "@rui/Stack";

const OuterContainer = styled(Stack)(({ theme }) => ({
  border: `1px dashed ${theme.palette.shades.neutral[800]}`,
  padding: theme.spacing(3),
  background: theme.palette.shades.neutral[800],
  minHeight: "150px",
  width: "100%",
  color: "#FFF",
  [theme.breakpoints.up("mobile")]: {
    borderRadius: theme.corners.base,
  },
}));

type NoRecordingReason =
  | "default"
  | "playback_disabled"
  | "upsell"
  | "unsupported_browser"
  | "unsupported_platform"
  | "restricted_upsell";

const CONTENT_BY_TYPE: {
  [key in NoRecordingReason]: {
    Icon: JSXElementConstructor<SvgIconProps>;
    title: string;
    content?: ReactNode;
  };
} = {
  default: {
    Icon: NoVideoIcon,
    title: "No recording available",
  },
  upsell: {
    Icon: NoVideoIcon,
    title: "No recording available",
    content: (
      <>
        <Link
          href={ROUTES.UPGRADE_ACCOUNT}
          sx={{ color: "shades.primary.300", fontWeight: 500 }}
        >
          Upgrade
        </Link>{" "}
        to Enterprise for audio and video playback on your next meeting
      </>
    ),
  },
  unsupported_browser: {
    Icon: AlertIcon,
    title: "Playback unavailable",
    content: "This browser does not support audio and video playback",
  },
  unsupported_platform: {
    Icon: NoVideoIcon,
    title: "No recording available",
    content: "Audio and video playback not supported on meeting platform",
  },
  playback_disabled: {
    Icon: NoVideoIcon,
    title: "Playback unavailable",
    content: "Audio and video playback is disabled for your Workspace",
  },
  restricted_upsell: {
    Icon: NoVideoIcon,
    title: "Playback unavailable",
    content: (
      <>
        <Link
          href={ROUTES.UPGRADE_ACCOUNT}
          sx={{ color: "shades.primary.300", fontWeight: 500 }}
        >
          Upgrade
        </Link>{" "}
        to Enterprise to view playback for this meeting.
      </>
    ),
  },
};

const NoRecordingCard = ({ type }: { type: NoRecordingReason }) => {
  const { Icon, title, content } = CONTENT_BY_TYPE[type];

  return (
    <OuterContainer spacing={1} variant="center">
      <IconBackground height={32} sx={{ bgcolor: "#FFFFFF4D" }}>
        <Icon sx={{ color: "#FFF" }} />
      </IconBackground>
      <Typography variant="bodyPrimaryMed">{title}</Typography>
      <Typography variant="bodyPrimaryReg" sx={{ textAlign: "center" }}>
        {content}
      </Typography>
    </OuterContainer>
  );
};

export default NoRecordingCard;
